import { useGetContentStyleByContentId } from '../editor/useGetContentStyleByContentId.tsx';
import type {
  AllTextDefaultStylesType,
  TableTextDefaultTagSelectors,
  TextDefaultTagSelectors,
} from 'components/editor/grid/reduxStore/textDefaultStylingSlice';
import {
  sharedTableStyles,
  TableDefaultClassNamesEnum,
  tableTextDefaultStyles as tableTextDefaultStylesDefaultValues,
  TextDefaultSelectorsEnum,
  textDefaultStyles as textDefaultStylesDefaultValues,
} from 'components/editor/grid/reduxStore/textDefaultStylingSlice';
import { tiptapDefaultStyle } from 'components/editor/GridDndEditor/TiptapEditor/tiptapDefaultStyle.ts';
import { mapTableStylesToAllBlocks } from '../../components/editor/TextDefaultStylesLoader/mapTableStylesToAllBlocks.ts';
import { tableRowDefaultStyle as tableRowDefaultStyleDefaultValues } from 'components/editor/grid/reduxStore/tableRowDefaultStyleSlice.ts';
import { listItemMarkerStyles, pricingTableTotalStyles } from '../../components/editor/TextDefaultStylesLoader/TextDefaultStylesLoader.tsx';

type AllStyles = Record<string, AllTextDefaultStylesType>;
export type DocumentStyle =
  | {
      canvas: Record<string, any>;
      textStyle: Record<string, any>;
      tableRowStyles: Record<string, any>;
    }
  | undefined;

export type DocumentStyleForPreviewProps = {
  documentStyle: DocumentStyle;
  documentStyleQueryStatus: string;
};

function groupTextAndTableTextStyles(styles: AllStyles) {
  const textKeys = Object.values(TextDefaultSelectorsEnum) as string[];
  const tableTextKeys = Object.values(TableDefaultClassNamesEnum) as string[];

  const textStyles: Partial<Record<TextDefaultTagSelectors, AllTextDefaultStylesType>> = {};
  const tableTextStyles: Partial<Record<TableDefaultClassNamesEnum, AllTextDefaultStylesType>> = {};

  Object.entries(styles).forEach(([key, value]) => {
    if (textKeys.includes(key)) {
      textStyles[key as TextDefaultTagSelectors] = value;
    } else if (tableTextKeys.includes(key)) {
      tableTextStyles[key as TableTextDefaultTagSelectors] = value;
    }
  });

  return { textStyles, tableTextStyles };
}

const useDocumentStyleForPreview = (contentId: string): DocumentStyleForPreviewProps => {
  const { data: documentStyleApiResponse, status: documentStyleQueryStatus } = useGetContentStyleByContentId(contentId);
  let documentStyle: DocumentStyle = undefined;

  if (documentStyleQueryStatus === 'success') {
    const { textDefaultStyles, tableDefaultStyles, ...documentSettingStyle } = documentStyleApiResponse;
    const { textStyles, tableTextStyles } = groupTextAndTableTextStyles(
      (textDefaultStyles || { ...tableTextDefaultStylesDefaultValues, ...textDefaultStylesDefaultValues }) as AllStyles
    );

    const { textBlockTableStyles, numberBlockStyles } = mapTableStylesToAllBlocks({
      ...tableTextDefaultStylesDefaultValues,
      ...tableTextStyles,
    });

    documentStyle = {
      canvas: documentSettingStyle,
      textStyle: {
        '.editor__page__draggable .tiptap': {
          ...textStyles,
          ...textBlockTableStyles,
        },
        ...listItemMarkerStyles(textStyles.p?.color),
        '.editor__page__draggable': {
          '.py-darkGrey-row .tiptap': { ...sharedTableStyles, fontWeight: 'bold' },
          '.py-total-row .tiptap p:not([data-table-style])': pricingTableTotalStyles,
          '.py-subtotal-row .tiptap p:not([data-table-style])': pricingTableTotalStyles,
          ...numberBlockStyles,
        },
        ...tiptapDefaultStyle,
      },
      tableRowStyles: {
        ':root {': {
          ...tableRowDefaultStyleDefaultValues,
          ...tableDefaultStyles,
        },
      },
    };
  }

  return { documentStyle, documentStyleQueryStatus };
};

export default useDocumentStyleForPreview;
